import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import * as turf from '@turf/turf';
import L from 'leaflet';
import { baseurl } from '../../baseurl';
import { 
  ArrowLeft, MapPin, User, Mail, Building, 
  Calendar, Download, CheckCircle, Clock, Save, Edit2, Map as MapIcon, Type
} from 'lucide-react';

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = 'pk.eyJ1IjoibWVhc3JvYWRtaW5tYXBib3giLCJhIjoiY21wYzQybXVrMDN5NDJ1c2JkZGhmYWFsNiJ9.aI89Wt7FHAYw3FUonCbKkw';
 





// =====================================================================
// Minimal manual GeoTIFF writer — bypasses UTIF.encodeImage entirely
// for the geo tags, since UTIF only recognizes a fixed whitelist of
// standard TIFF tag numbers and rejects unknown ones like 33550/33922
// (GeoTIFF's ModelPixelScaleTag / ModelTiepointTag). This writes a
// baseline, uncompressed RGBA TIFF with those geo tags embedded
// directly, following the TIFF 6.0 IFD binary layout.
// =====================================================================
function writeGeoTIFF({ rgba, width, height, pixelSizeX, pixelSizeY, topLeftLng, topLeftLat }) {
  const TAG_TYPE = { SHORT: 3, LONG: 4, RATIONAL: 5, DOUBLE: 12 };

  // Each tag entry: { tag, type, count, values }
  // Values for DOUBLE/arrays >1 elem always go to the external data area
  // (can't fit inline in the 4-byte value/offset slot), per spec.
  const entries = [
    { tag: 256, type: TAG_TYPE.LONG, count: 1, values: [width] },          // ImageWidth
    { tag: 257, type: TAG_TYPE.LONG, count: 1, values: [height] },        // ImageLength
    { tag: 258, type: TAG_TYPE.SHORT, count: 4, values: [8, 8, 8, 8] },   // BitsPerSample
    { tag: 259, type: TAG_TYPE.SHORT, count: 1, values: [1] },            // Compression: none
    { tag: 262, type: TAG_TYPE.SHORT, count: 1, values: [2] },            // Photometric: RGB
    { tag: 273, type: TAG_TYPE.LONG, count: 1, values: [0] },             // StripOffsets (patched later)
    { tag: 277, type: TAG_TYPE.SHORT, count: 1, values: [4] },            // SamplesPerPixel
    { tag: 278, type: TAG_TYPE.LONG, count: 1, values: [height] },        // RowsPerStrip
    { tag: 279, type: TAG_TYPE.LONG, count: 1, values: [width * height * 4] }, // StripByteCounts
    { tag: 284, type: TAG_TYPE.SHORT, count: 1, values: [1] },            // PlanarConfig
    { tag: 338, type: TAG_TYPE.SHORT, count: 1, values: [2] },            // ExtraSamples: unassoc alpha
    { tag: 33550, type: TAG_TYPE.DOUBLE, count: 3, values: [Math.abs(pixelSizeX), Math.abs(pixelSizeY), 0] }, // ModelPixelScaleTag
    { tag: 33922, type: TAG_TYPE.DOUBLE, count: 6, values: [0, 0, 0, topLeftLng, topLeftLat, 0] }, // ModelTiepointTag
  ].sort((a, b) => a.tag - b.tag); // IFD entries MUST be in ascending tag order

  const typeSize = { [TAG_TYPE.SHORT]: 2, [TAG_TYPE.LONG]: 4, [TAG_TYPE.RATIONAL]: 8, [TAG_TYPE.DOUBLE]: 8 };

  // Compute layout: header(8) + IFD(2 + entries*12 + 4) + external data + pixel data
  const ifdEntryCount = entries.length;
  const ifdSize = 2 + ifdEntryCount * 12 + 4;
  let externalDataOffset = 8 + ifdSize;

  // Assign each entry either an inline value or an external offset
  const externalChunks = [];
entries.forEach((e) => {
  const perValueBytes = typeSize[e.type]; // 2 for SHORT, 4 for LONG, 8 for DOUBLE
  const totalBytes = perValueBytes * e.count;
  const inlineCapacity = 4;

  if (totalBytes <= inlineCapacity && e.type !== TAG_TYPE.DOUBLE) {
    e._inline = true;
  } else {
    e._inline = false;
    e._offset = externalDataOffset;
    externalChunks.push(e);
    externalDataOffset += totalBytes; // now correctly type-aware
    if (externalDataOffset % 2 !== 0) externalDataOffset += 1; // word alignment
  }
});

  const pixelDataOffset = externalDataOffset;
  // Patch StripOffsets now that we know where pixel data begins
  entries.find((e) => e.tag === 273).values = [pixelDataOffset];

  const totalSize = pixelDataOffset + width * height * 4;
  const buf = new ArrayBuffer(totalSize);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);

  // --- Header: little-endian, magic 42, offset to first IFD ---
  view.setUint8(0, 0x49); // 'I'
  view.setUint8(1, 0x49); // 'I' -> little-endian
  view.setUint16(2, 42, true);
  view.setUint32(4, 8, true); // first IFD at byte 8

  // --- IFD ---
  let p = 8;
  view.setUint16(p, ifdEntryCount, true);
  p += 2;

  entries.forEach((e) => {
    view.setUint16(p, e.tag, true);
    view.setUint16(p + 2, e.type, true);
    view.setUint32(p + 4, e.count, true);

    if (e._inline) {
      // Write inline values packed into the 4-byte slot
      let off = p + 8;
      e.values.forEach((v) => {
        if (e.type === TAG_TYPE.SHORT) { view.setUint16(off, v, true); off += 2; }
        else if (e.type === TAG_TYPE.LONG) { view.setUint32(off, v, true); off += 4; }
      });
    } else {
      view.setUint32(p + 8, e._offset, true);
    }
    p += 12;
  });
  view.setUint32(p, 0, true); // next IFD offset = 0 (none)

  // --- External data (DOUBLE arrays etc.) ---
externalChunks.forEach((e) => {
  let off = e._offset;
  e.values.forEach((v) => {
    if (e.type === TAG_TYPE.SHORT) {
      view.setUint16(off, v, true);
      off += 2;
    } else if (e.type === TAG_TYPE.LONG) {
      view.setUint32(off, v, true);
      off += 4;
    } else if (e.type === TAG_TYPE.DOUBLE) {
      view.setFloat64(off, v, true);
      off += 8;
    }
  });
});

  // --- Pixel data (raw RGBA, uncompressed) ---
  bytes.set(rgba, pixelDataOffset);

  return buf;
}






// =====================================================================
// Nearmap HD Parcel Download — constants + tile helpers
// =====================================================================
const NEARMAP_API_KEY = 'NWI2ZWIzNTYtODdmMS00YmIyLTk5ZGItZDkwNTVjMDM2MzFi';
const NEARMAP_CONTENT_TYPE = 'Vert';
const NEARMAP_TILE_SIZE = 256;
// const DOWNLOAD_BUFFER_METERS = 5;
// const DOWNLOAD_ZOOM = 21;


const DOWNLOAD_ZOOM = 22;          // Nearmap's highest available tile zoom
const DOWNLOAD_SCALE = 2;          // 2x supersample — sharper output, esp. after any resizing
const DOWNLOAD_MAX_CANVAS_DIM = 12000; // raised ceiling to allow the larger ultra-HD canvas
const DOWNLOAD_ZOOM_MAX = 22;       // Nearmap's highest available tile zoom
const DOWNLOAD_ZOOM_MIN = 17;       // floor — still HD, just less than max for big areas
// const DOWNLOAD_SCALE = 2;           // 2x supersample
// const DOWNLOAD_MAX_CANVAS_DIM = 12000;


const DOWNLOAD_BUFFER_METERS = 20;



const lon2tileX = (lon, z) => (lon + 180) / 360 * Math.pow(2, z);
const lat2tileY = (lat, z) => {
  const rad = lat * Math.PI / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, z);
};
const tile2lon = (x, z) => (x / Math.pow(2, z)) * 360 - 180;
const tile2lat = (y, z) => {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

const nearmapTileUrl = (z, x, y) =>
  `https://api.nearmap.com/tiles/v3/${NEARMAP_CONTENT_TYPE}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`;

function loadTileImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// =====================================================================
// UTIF loader — only fetched when the user actually picks TIFF, so it
// never adds weight to the normal PNG/JPEG download path
// =====================================================================
let utifLoadPromise = null;
function ensureUTIF() {
  if (window.UTIF) return Promise.resolve(window.UTIF);
  if (utifLoadPromise) return utifLoadPromise;
  utifLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/utif@3.1.0/UTIF.js';
    script.onload = () => resolve(window.UTIF);
    script.onerror = () => reject(new Error('Failed to load UTIF'));
    document.head.appendChild(script);
  });
  return utifLoadPromise;
}

// =====================================================================
// Composite the buffered parcel bbox at high zoom into one canvas, and
// return everything needed both to export the image AND to write
// correct geo metadata (top-left lng/lat, pixel size in degrees).
// =====================================================================
// compositeParcelForDownload — pick the highest zoom (up to DOWNLOAD_ZOOM_MAX)
// that still fits within DOWNLOAD_MAX_CANVAS_DIM at the given scale, instead
// of throwing when the fixed zoom-22 canvas would be too large
async function compositeParcelForDownload(featureCollection) {
  const bbox = turf.bbox(featureCollection);
  const bboxPolygon = turf.bboxPolygon(bbox);
  const buffered = turf.buffer(bboxPolygon, DOWNLOAD_BUFFER_METERS, { units: 'meters' });
  const bufferedBbox = turf.bbox(buffered);

  const SCALE = DOWNLOAD_SCALE;

  // Find the largest zoom level (<= DOWNLOAD_ZOOM_MAX) whose resulting
  // canvas fits within DOWNLOAD_MAX_CANVAS_DIM — walks down from the max
  // native resolution instead of hard-failing on big parcels
  let z = DOWNLOAD_ZOOM_MAX;
  let worldMinX, worldMaxX, worldMinY, worldMaxY, canvasW, canvasH;

  while (z >= DOWNLOAD_ZOOM_MIN) {
    worldMinX = lon2tileX(bufferedBbox[0], z) * NEARMAP_TILE_SIZE;
    worldMaxX = lon2tileX(bufferedBbox[2], z) * NEARMAP_TILE_SIZE;
    worldMinY = lat2tileY(bufferedBbox[3], z) * NEARMAP_TILE_SIZE;
    worldMaxY = lat2tileY(bufferedBbox[1], z) * NEARMAP_TILE_SIZE;

    canvasW = Math.ceil((worldMaxX - worldMinX) * SCALE);
    canvasH = Math.ceil((worldMaxY - worldMinY) * SCALE);

    if (canvasW <= DOWNLOAD_MAX_CANVAS_DIM && canvasH <= DOWNLOAD_MAX_CANVAS_DIM) {
      break; // this zoom fits — use it
    }
    z -= 1; // too big — try one zoom level lower (each step ~halves the canvas)
  }

  if (canvasW <= 0 || canvasH <= 0 || canvasW > DOWNLOAD_MAX_CANVAS_DIM || canvasH > DOWNLOAD_MAX_CANVAS_DIM) {
    // Even the floor zoom didn't fit — genuinely too large, but this
    // should now only happen for extremely large multi-km areas
    throw new Error(`Parcel area too large to render even at zoom ${DOWNLOAD_ZOOM_MIN} — try a smaller boundary or split it into multiple exports`);
  }

  console.log(`Ultra-HD export using zoom ${z} (canvas ${canvasW}x${canvasH})`);

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const xMin = Math.floor(worldMinX / NEARMAP_TILE_SIZE);
  const xMax = Math.floor(worldMaxX / NEARMAP_TILE_SIZE);
  const yMin = Math.floor(worldMinY / NEARMAP_TILE_SIZE);
  const yMax = Math.floor(worldMaxY / NEARMAP_TILE_SIZE);

  const tileCoords = [];
  for (let tx = xMin; tx <= xMax; tx++) {
    for (let ty = yMin; ty <= yMax; ty++) tileCoords.push([tx, ty]);
  }

  const CONCURRENCY = 10;
  for (let i = 0; i < tileCoords.length; i += CONCURRENCY) {
    const batch = tileCoords.slice(i, i + CONCURRENCY);
    const loaded = await Promise.all(
      batch.map(([tx, ty]) => loadTileImage(nearmapTileUrl(z, tx, ty)).then((img) => ({ tx, ty, img })))
    );
    for (const { tx, ty, img } of loaded) {
      if (!img) continue;
      const dx = (tx * NEARMAP_TILE_SIZE - worldMinX) * SCALE;
      const dy = (ty * NEARMAP_TILE_SIZE - worldMinY) * SCALE;
      ctx.drawImage(img, dx, dy, NEARMAP_TILE_SIZE * SCALE, NEARMAP_TILE_SIZE * SCALE);
    }
    await new Promise((r) => setTimeout(r, 0));
  }

  const topLeftLng = tile2lon(worldMinX / NEARMAP_TILE_SIZE, z);
  const topLeftLat = tile2lat(worldMinY / NEARMAP_TILE_SIZE, z);
  const bottomRightLng = tile2lon(worldMaxX / NEARMAP_TILE_SIZE, z);
  const bottomRightLat = tile2lat(worldMaxY / NEARMAP_TILE_SIZE, z);

  const pixelSizeX = (bottomRightLng - topLeftLng) / canvasW;
  const pixelSizeY = (bottomRightLat - topLeftLat) / canvasH;

  return {
    canvas,
    ctx,
    width: canvasW,
    height: canvasH,
    topLeftLng,
    topLeftLat,
    pixelSizeX,
    pixelSizeY,
    bufferedBbox,
    zoomUsed: z, // useful to surface in UI, e.g. "exported at zoom 19 due to parcel size"
  };
}

// =====================================================================
// World file (.pgw / .jgw / .tfw) — the standard, universally-supported
// way to attach correct geographic coordinates to a plain raster image
// so it opens correctly georeferenced in GIS tools (QGIS, ArcGIS, etc.)
// =====================================================================
function buildWorldFileContent({ pixelSizeX, pixelSizeY, topLeftLng, topLeftLat }) {
  // Line order per world file spec:
  // pixel size X, rotation (0), rotation (0), pixel size Y (negative), top-left X, top-left Y
  return [
    pixelSizeX.toFixed(12),
    '0.0000000000',
    '0.0000000000',
    pixelSizeY.toFixed(12),
    topLeftLng.toFixed(12),
    topLeftLat.toFixed(12),
  ].join('\n');
}

function worldFileExtensionFor(format) {
  if (format === 'png') return 'pgw';
  if (format === 'tif') return 'tfw';
  return 'jgw'; // jpeg / jpg
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// =====================================================================
// Format-specific export — PNG embeds coordinate metadata as a tEXt
// chunk; JPEG/JPG embed EXIF GPS tags; TIFF is a real georeferenced
// GeoTIFF written via UTIF (embeds ModelPixelScale/ModelTiepoint tags).
// All formats also get a companion world file for maximum GIS
// compatibility.
// =====================================================================
function insertPngTextChunk(pngArrayBuffer, keyword, text) {
  const bytes = new Uint8Array(pngArrayBuffer);
  const crcTable = (() => {
    let c;
    const table = [];
    for (let n = 0; n < 256; n++) {
      c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      table[n] = c;
    }
    return table;
  })();
  const crc32 = (buf) => {
    let c = 0xffffffff;
    for (let i = 0; i < buf.length; i++) c = crcTable[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  };

  const textBytes = new TextEncoder().encode(`${keyword}\0${text}`);
  const chunkType = new TextEncoder().encode('tEXt');
  const lenBuf = new Uint8Array(4);
  new DataView(lenBuf.buffer).setUint32(0, textBytes.length);

  const crcInput = new Uint8Array(chunkType.length + textBytes.length);
  crcInput.set(chunkType, 0);
  crcInput.set(textBytes, chunkType.length);
  const crcBuf = new Uint8Array(4);
  new DataView(crcBuf.buffer).setUint32(0, crc32(crcInput));

  // Insert right after the IHDR chunk (bytes 8..33)
  const insertAt = 33;
  const chunk = new Uint8Array(4 + 4 + textBytes.length + 4);
  chunk.set(lenBuf, 0);
  chunk.set(chunkType, 4);
  chunk.set(textBytes, 8);
  chunk.set(crcBuf, 8 + textBytes.length);

  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.slice(0, insertAt), 0);
  out.set(chunk, insertAt);
  out.set(bytes.slice(insertAt), insertAt + chunk.length);
  return out.buffer;
}

function toExifGPSRational(value) {
  const abs = Math.abs(value);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = (minFloat - min) * 60;
  return { deg, min, sec, ref: value };
}

async function exportParcelImage(featureCollection, format, filenameBase) {
  const composite = await compositeParcelForDownload(featureCollection);
  const { canvas, width, height, topLeftLng, topLeftLat, pixelSizeX, pixelSizeY } = composite;

  const worldFileContent = buildWorldFileContent({ pixelSizeX, pixelSizeY, topLeftLng, topLeftLat });
  const worldFileExt = worldFileExtensionFor(format);

  if (format === 'png') {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    const buf = await blob.arrayBuffer();
    const geoNote = `bbox=${topLeftLng},${topLeftLat},${topLeftLng + pixelSizeX * width},${topLeftLat + pixelSizeY * height}; pixelSizeX=${pixelSizeX}; pixelSizeY=${pixelSizeY}`;
    const withMeta = insertPngTextChunk(buf, 'GeoBounds', geoNote);
    triggerBlobDownload(new Blob([withMeta], { type: 'image/png' }), `${filenameBase}.png`);
    triggerBlobDownload(new Blob([worldFileContent], { type: 'text/plain' }), `${filenameBase}.${worldFileExt}`);
    return;
  }

  if (format === 'jpeg' || format === 'jpg') {
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 1.0));
    const buf = new Uint8Array(await blob.arrayBuffer());

    // Minimal EXIF GPS IFD injection — writes correct lat/lng of the
    // image's top-left corner into standard GPS EXIF tags
    const lngRef = topLeftLng < 0 ? 'W' : 'E';
    const latRef = topLeftLat < 0 ? 'S' : 'N';
    const lngDMS = toExifGPSRational(topLeftLng);
    const latDMS = toExifGPSRational(topLeftLat);

    // Fallback: embed the same info as a simple JPEG COM (comment) segment
    // for broad compatibility, in addition to the world file
    const comment = `GeoTopLeft=${latDMS.deg}D${latDMS.min}M${latDMS.sec.toFixed(3)}S ${latRef}, ${lngDMS.deg}D${lngDMS.min}M${lngDMS.sec.toFixed(3)}S ${lngRef}; pixelSizeX=${pixelSizeX}; pixelSizeY=${pixelSizeY}`;
    const commentBytes = new TextEncoder().encode(comment);
    const segLen = commentBytes.length + 2;
    const comSegment = new Uint8Array(4 + commentBytes.length);
    comSegment[0] = 0xff;
    comSegment[1] = 0xfe; // COM marker
    comSegment[2] = (segLen >> 8) & 0xff;
    comSegment[3] = segLen & 0xff;
    comSegment.set(commentBytes, 4);

    // Insert right after SOI (0xFFD8)
    const out = new Uint8Array(buf.length + comSegment.length);
    out.set(buf.slice(0, 2), 0);
    out.set(comSegment, 2);
    out.set(buf.slice(2), 2 + comSegment.length);

    const ext = format === 'jpg' ? 'jpg' : 'jpeg';
    triggerBlobDownload(new Blob([out], { type: 'image/jpeg' }), `${filenameBase}.${ext}`);
    triggerBlobDownload(new Blob([worldFileContent], { type: 'text/plain' }), `${filenameBase}.${worldFileExt}`);
    return;
  }

// =====================================================================
// exportParcelImage — TIFF branch now uses the manual GeoTIFF writer
// instead of UTIF.encodeImage (no UTIF dependency needed at all for
// the TIFF path anymore)
// =====================================================================
// exportParcelImage — TIFF branch: for very large canvases, getImageData()
// on the full canvas at once can hit browser memory limits even when the
// canvas itself was small enough to create. Read it in horizontal strips
// and assemble the full RGBA buffer, instead of one giant getImageData call.
if (format === 'tif') {
  const { width, height } = composite;
  const STRIP_HEIGHT = 512; // read in manageable chunks instead of the whole image at once
  const rgba = new Uint8ClampedArray(width * height * 4);

  for (let y = 0; y < height; y += STRIP_HEIGHT) {
    const stripH = Math.min(STRIP_HEIGHT, height - y);
    const stripData = composite.ctx.getImageData(0, y, width, stripH).data;
    rgba.set(stripData, y * width * 4);
    await new Promise((r) => setTimeout(r, 0)); // yield between strips
  }

  const tiffBuf = writeGeoTIFF({
    rgba,
    width,
    height,
    pixelSizeX: composite.pixelSizeX,
    pixelSizeY: composite.pixelSizeY,
    topLeftLng: composite.topLeftLng,
    topLeftLat: composite.topLeftLat,
  });
  triggerBlobDownload(new Blob([tiffBuf], { type: 'image/tiff' }), `${filenameBase}.tif`);
  triggerBlobDownload(new Blob([buildWorldFileContent(composite)], { type: 'text/plain' }), `${filenameBase}.tfw`);
  return;
}

  throw new Error(`Unsupported format: ${format}`);
}
























// --- helper: same coordinate-order correction as ProjectDetails, returns area in SQUARE METERS ---
const calculateAreaSqM = (featureColl) => {
  let totalSqM = 0;
  if (!featureColl?.features) return 0;

  featureColl.features.forEach(feature => {
    if (!feature?.geometry) return;
    try {
      const type = feature.geometry.type;
      if (type !== 'Polygon' && type !== 'MultiPolygon') return;

      const rawCoords = type === 'Polygon'
        ? feature.geometry.coordinates[0]
        : feature.geometry.coordinates[0][0];

      const firstPoint = rawCoords?.[0];
      let correctedFeature = feature;

      // US longitudes are negative; if first value is 0-90 it's likely [lat, lng] swapped
      if (firstPoint && firstPoint[0] > 0 && firstPoint[0] < 90) {
        const swapRing = ring => ring.map(c => [c[1], c[0]]);
        correctedFeature = {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: type === 'Polygon'
              ? feature.geometry.coordinates.map(swapRing)
              : feature.geometry.coordinates.map(poly => poly.map(swapRing)),
          }
        };
      }

      totalSqM += turf.area(correctedFeature);
    } catch (err) {
      console.error('Error calculating area:', err);
    }
  });

  return totalSqM;
};




const formatToFeatureCollection = (geo) => {
  if (!geo) return null;
  try {
    if (Array.isArray(geo)) return turf.featureCollection(geo);
    if (geo.type === 'FeatureCollection') return geo;
    if (geo.type === 'Feature') return turf.featureCollection([geo]);
    if (geo.type === 'Polygon' || geo.type === 'MultiPolygon') return turf.featureCollection([turf.feature(geo)]);
    return null;
  } catch (e) { return null; }
};
















const SubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  const [editedStatus, setEditedStatus] = useState('');
  const [editedReportType, setEditedReportType] = useState('');
  const [editedGeometry, setEditedGeometry] = useState('');
  const [editedInstructions, setEditedInstructions] = useState('');
  const [editedAddress, setEditedAddress] = useState("");
  
  const [isManualArea, setIsManualArea] = useState(false);
  const [manualArea, setManualArea] = useState('');

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);



  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
const [downloadFormat, setDownloadFormat] = useState('jpeg');
const [isDownloading, setIsDownloading] = useState(false);



const handleDownloadParcelImage = async () => {
  try {
    const parsed = JSON.parse(editedGeometry);
    const fc = formatToFeatureCollection(parsed);
    if (!fc || fc.features.length === 0) {
      Swal.fire('No boundary', 'There is no valid parcel geometry to export.', 'warning');
      return;
    }
    setIsDownloading(true);
    const filenameBase = `parcel_${data?.property_id || id}_${Date.now()}`;
    await exportParcelImage(fc, downloadFormat, filenameBase);
    setShowDownloadPanel(false);
  } catch (err) {
    console.error('Parcel image download failed:', err);
    Swal.fire('Error', 'Could not generate the parcel image. ' + (err.message || ''), 'error');
  } finally {
    setIsDownloading(false);
  }
};






  const [metrics, setMetrics] = useState({
    area: '0',
    credits: '0',
    time: '0'
  });

  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchDetail();
    fetchPackages();
  }, [id, adminToken]);

  const fetchDetail = async () => {
    try {
      const response = await axios.get(`${baseurl}getSubmissionByIdAdmin/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (response.data.success) {
        const item = response.data.data;
        setData(item);
        
        setEditedStatus(item.status);
        setEditedReportType(item.project_data?.reportType || '');
        setEditedGeometry(JSON.stringify(item.geometry, null, 2));
        setEditedInstructions(item.project_data?.instructions?.join('\n') || '');
        setEditedAddress(item.project_data?.address || "");
        setManualArea(item.project_data?.lotArea || '');
        
        setMetrics({
          area: item.project_data?.lotArea || '0',
          credits: item.project_data?.totalCredits || '0',
          time: item.project_data?.estimatedTime || '0'
        });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch details' });
    } finally {
      setLoading(false);
    }
  };

  // --- MAP INITIALIZATION ---
// --- MAP INITIALIZATION (FIXED: force resize so canvas isn't blank) ---
// --- MAP INITIALIZATION (WITH DEBUG LOGGING) ---
useEffect(() => {
    console.log('[MAP INIT] effect fired. container:', mapContainerRef.current, 'existing instance:', mapInstanceRef.current);

    if (loading) {
        console.log('[MAP INIT] ABORTED: still loading, container not rendered yet');
        return;
    }
    if (!mapContainerRef.current) {
        console.log('[MAP INIT] ABORTED: container ref is null');
        return;
    }
    if (mapInstanceRef.current) {
        console.log('[MAP INIT] ABORTED: map instance already exists');
        return;
    }

    console.log('[MAP INIT] container size at init:', {
        width: mapContainerRef.current.offsetWidth,
        height: mapContainerRef.current.offsetHeight
    });

    const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: [-83.047, 42.333],
        zoom: 14,
    });

    map.on('load', () => {
        console.log('[MAP EVENT] "load" fired');
        map.resize();
    });

    map.on('error', (e) => {
        console.error('[MAP EVENT] "error" fired:', e?.error || e);
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapInstanceRef.current = map;

    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(mapContainerRef.current);

    return () => {
        resizeObserver.disconnect();
        map.remove();
        mapInstanceRef.current = null;
    };
}, [loading]); // <-- KEY FIX: re-run once `loading` flips to false

  // --- DRAW GEOMETRY ---
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) {
        console.log('[DRAW GEO] ABORTED: no map instance yet');
        return;
    }

    const drawGeo = () => {
        console.log('[DRAW GEO] drawGeo() running. isStyleLoaded:', map.isStyleLoaded());
        ['submission-fill', 'submission-line'].forEach(layerId => {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
        });
        if (map.getSource('submission-geo')) map.removeSource('submission-geo');

        try {
            const parsed = JSON.parse(editedGeometry);
            const fc = formatToFeatureCollection(parsed);
            console.log('[DRAW GEO] parsed feature collection:', fc);
            if (!fc || fc.features.length === 0) {
                console.log('[DRAW GEO] ABORTED: empty/invalid feature collection');
                return;
            }

            map.addSource('submission-geo', { type: 'geojson', data: fc });
            map.addLayer({
                id: 'submission-fill', source: 'submission-geo', type: 'fill',
                paint: { 'fill-color': '#4318FF', 'fill-opacity': 0.2 }
            });
            map.addLayer({
                id: 'submission-line', source: 'submission-geo', type: 'line',
                paint: { 'line-color': '#4318FF', 'line-width': 3 }
            });

            const allCoords = fc.features.flatMap(f => {
                const g = f.geometry;
                if (g.type === 'Polygon') return g.coordinates[0];
                if (g.type === 'MultiPolygon') return g.coordinates.flatMap(p => p[0]);
                return [];
            });
            console.log('[DRAW GEO] total coords for bounds:', allCoords.length);
            if (allCoords.length > 0) {
                const bounds = allCoords.reduce(
                    (b, c) => b.extend(c),
                    new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
                );
                map.fitBounds(bounds, { padding: 60, animate: true });
            }
        } catch (e) {
            console.error('[DRAW GEO] JSON parse / draw error:', e);
        }
    };

    if (map.isStyleLoaded()) {
        drawGeo();
    } else {
        console.log('[DRAW GEO] style not loaded yet, waiting for "load" event');
        map.once('load', drawGeo);
    }
}, [editedGeometry]);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllServicePackages`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (res.data.success) setPackages(res.data.data);
    } catch (err) { console.error("Packages load fail", err); }
  };

  // --- CORE CALCULATION LOGIC (FIXED: consistent sqm-based conversion) ---
useEffect(() => {
    if (!isEditing || packages.length === 0) return;
    try {
        const pkg = packages.find(p => p.name === editedReportType);
        if (!pkg) return;

        let areaSqM = 0;
        let currentSqft = 0;

        if (isManualArea) {
            // manual input is entered in Sqft, so convert sqft -> sqm for consistency
            currentSqft = parseFloat(manualArea.replace(/[^0-9.]/g, '')) || 0;
            areaSqM = currentSqft / 10.7639;
        } else {
            const rawGeo = JSON.parse(editedGeometry);
            const featureColl = formatToFeatureCollection(rawGeo);
            if (featureColl) {
                areaSqM = calculateAreaSqM(featureColl);
                currentSqft = areaSqM * 10.7639;
            }
        }

        // FIX: convert from square meters to acres (was incorrectly dividing sqft by 4046.86)
        const areaAcres = areaSqM / 4046.86;

        const baseUnit = parseFloat(pkg.base_unit_acre) || 1;
        const creditRate = parseFloat(pkg.credit_rate) || 1;
        const timeValue = parseFloat(pkg.est_time_value) || 30;

        const totalCredits = (areaAcres / baseUnit) * creditRate;
        const totalTimeMins = (areaAcres / baseUnit) * timeValue;
        const totalTimeHours = (totalTimeMins / 60).toFixed(1);

        const areaString = isManualArea
            ? manualArea
            : `${Math.round(currentSqft).toLocaleString()} Sqft`;

        setMetrics({
            area: areaString,
            credits: totalCredits.toFixed(2),
            time: `≈ ${totalTimeHours} Hours`
        });

        if (!isManualArea) setManualArea(areaString);
    } catch (e) { /* typing */ }
}, [editedGeometry, editedReportType, isEditing, packages, isManualArea, manualArea]);

  const handleSave = async () => {
    try {
      const parsedGeo = JSON.parse(editedGeometry);
      const updatedPayload = {
        status: editedStatus,
        geometry: parsedGeo,
        project_data: {
          ...data.project_data,
          address: editedAddress,
          reportType: editedReportType,
          lotArea: metrics.area, 
          totalCredits: metrics.credits,
          estimatedTime: metrics.time,
          instructions: editedInstructions.split('\n').filter(i => i.trim() !== '')
        }
      };

      const response = await axios.put(`${baseurl}updateSubmissionAdmin/${id}`, updatedPayload, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (response.data.success) {
        Swal.fire('Saved!', 'Geometry and calculations updated.', 'success');
        setIsEditing(false);
        fetchDetail();
      }
    } catch (err) {
      Swal.fire('Error', 'Invalid JSON Geometry. Check for trailing commas.', 'error');
    }
  };

  // --- LOADING / EMPTY STATES (no early return before hooks, but before JSX using `data`) ---
  if (loading) {
    return (
      <div className="app-container">
        <Topbar />
        <div className="p-5 text-center">Loading Details...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-container">
        <Topbar />
        <div className="p-5 text-center">No data found.</div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
        <div className="profile-content-container p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="d-flex align-items-center mb-4">
            <button onClick={() => navigate(-1)} className="btn btn-white shadow-sm rounded-circle me-3">
              <ArrowLeft size={20} />
            </button>
            <div className="me-auto">
              <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>Admin Review</h3>
              <p className="text-muted small m-0">Project ID: {data?.property_id}</p>
            </div>
            <div className="d-flex gap-2">
              {!isEditing ? (
                <button className="btn btn-primary d-flex align-items-center rounded-pill px-4 shadow-sm" onClick={() => setIsEditing(true)}>
                  <Edit2 size={18} className="me-2" /> Modify Submission
                </button>
              ) : (
                <>
                  <button className="btn btn-light rounded-pill px-4" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button className="btn btn-success rounded-pill px-4 shadow-sm" onClick={handleSave}>
                    <Save size={18} className="me-2" /> Save Changes
                  </button>
                </>
              )}
            </div>
          </div>




          {/* Download HD Parcel Image button — placed next to Modify Submission */}
<div className="d-flex gap-2 position-relative">
  <button
    className="btn btn-outline-primary d-flex align-items-center rounded-pill px-4 shadow-sm"
    onClick={() => setShowDownloadPanel((v) => !v)}
  >
    <Download size={18} className="me-2" /> Download HD Image
  </button>

  {showDownloadPanel && (
    <div
      style={{
        position: 'absolute', top: '110%', right: 0, width: '260px',
        background: '#fff', borderRadius: '14px', padding: '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 2000,
      }}
    >
      <div className="fw-bold mb-2" style={{ fontSize: '13px' }}>Image Format</div>
      <select
        className="form-select mb-3"
        value={downloadFormat}
        onChange={(e) => setDownloadFormat(e.target.value)}
        style={{ fontSize: '13px' }}
      >
        <option value="jpeg">JPEG (.jpeg)</option>
        <option value="jpg">JPG (.jpg)</option>
        <option value="png">PNG (.png)</option>
        <option value="tif">TIFF / GeoTIFF (.tif)</option>
      </select>
      <button
        className="btn btn-primary w-100 rounded-pill"
        disabled={isDownloading}
        onClick={handleDownloadParcelImage}
      >
        {isDownloading ? 'Generating…' : 'Download'}
      </button>
     <div className="text-muted mt-2" style={{ fontSize: '10px' }}>
  Ultra HD export (zoom {DOWNLOAD_ZOOM}, {DOWNLOAD_SCALE}x supersample). Includes a companion world file with exact coordinates for GIS use.
</div>
    </div>
  )}
</div>



          <div className="row g-4">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: '20px', height: '400px', position: 'relative' }}>
  <div ref={mapContainerRef} style={{ height: '100%', width: '100%', borderRadius: '20px' }} />
</div>












              <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '20px' }}>
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <MapPin size={20} className="me-2"/> Boundary Data
                </h5>
                
                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <label className="text-muted small fw-bold">PROPERTY ADDRESS</label>
                    {isEditing ? (
                      <input 
                        type="text" 
                        className="form-control bg-white border-primary" 
                        value={editedAddress} 
                        onChange={(e) => setEditedAddress(e.target.value)}
                        required 
                      />
                    ) : (
                      <input 
                        type="text" 
                        className="form-control bg-light border-0" 
                        value={data?.project_data?.address || ""}
                        readOnly 
                      />
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small fw-bold">REPORT TYPE</label>
                    {isEditing ? (
                      <select className="form-select border-primary" value={editedReportType} onChange={(e) => setEditedReportType(e.target.value)}>
                        {packages.map(pkg => <option key={pkg.id} value={pkg.name}>{pkg.name}</option>)}
                      </select>
                    ) : <p className="fw-bold">{data?.project_data?.reportType}</p>}
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small fw-bold">SUBMISSION STATUS</label>
                    {isEditing ? (
                      <select className="form-select border-primary" value={editedStatus} onChange={(e) => setEditedStatus(e.target.value)}>
                        <option value="Draft">Draft</option>
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                    ) : <div><span className={`badge-status ${data?.status?.toLowerCase()}`}>{data?.status}</span></div>}
                  </div>
                </div>

                <label className="text-muted small fw-bold mb-2">RAW GEOJSON</label>
                <textarea 
                  className={`form-control font-monospace ${isEditing ? 'border-primary' : 'bg-dark text-info border-0'}`}
                  style={{ height: '220px', fontSize: '11px' }}
                  value={editedGeometry}
                  onChange={(e) => setEditedGeometry(e.target.value)}
                  readOnly={!isEditing}
                />
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card border-0 shadow-sm p-4 bg-primary text-white mb-4" style={{ borderRadius: '20px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold m-0 opacity-75">Calculated Stats</h6>
                  {isEditing && (
                    <button 
                      className={`btn btn-sm ${isManualArea ? 'btn-info' : 'btn-outline-light'} py-0 px-2`} 
                      style={{ fontSize: '11px', borderRadius: '5px' }}
                      onClick={() => setIsManualArea(!isManualArea)}
                    >
                      {isManualArea ? <Type size={12} className="me-1"/> : <MapIcon size={12} className="me-1"/>} 
                      {isManualArea ? 'Manual' : 'Switch Manual'}
                    </button>
                  )}
                </div>
                
                <div className="mb-4">
                   <div className="small opacity-75 mb-1">Lot Area</div>
                  {isEditing && isManualArea ? (
  <input 
    type="text" 
    className="form-control bg-white text-primary fw-bold border-0 shadow-none" 
    value={manualArea}
    onChange={(e) => setManualArea(e.target.value)}
    placeholder="e.g. 15,000 Sqft"
  />
) : (
  <div className="h3 fw-bold">
    {metrics.area}
    {metrics.area && (
      <span className="fs-6 fw-normal opacity-75 ms-2">
        ({(parseFloat(metrics.area.replace(/,/g, '')) / 43560).toFixed(2)} acres)
      </span>
    )}
  </div>
)}
                </div>

                <div className="d-flex justify-content-between mb-2">
                  <span className="small opacity-75">Credits Charged</span>
                  <span className="fw-bold">{metrics.credits} CR</span>
                </div>
                {/* <div className="d-flex justify-content-between mb-4">
                  <span className="small opacity-75">Delivery Time</span>
                  <span className="fw-bold"><Clock size={14} className="me-1"/> {metrics.time}</span>
                </div> */}
                <hr className="bg-white opacity-25" />
                <a href={`${baseurl.replace('/api/', '')}${data?.project_data?.sitemap_url}`} target="_blank" rel="noreferrer" className="btn btn-white w-100 rounded-pill fw-bold text-primary py-2">
                  <Download size={16} className="me-2" /> View Original Sitemap
                </a>
              </div>

              <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '20px' }}>
                <div className="d-flex align-items-center mb-4">
                  <div className="avatar-large me-3"><User size={24} /></div>
                  <div>
                    <h6 className="fw-bold m-0">{data?.full_name}</h6>
                    <span className="text-muted small text-primary">{data?.company_name}</span>
                  </div>
                </div>
                <div className="small border-top pt-3">
                  <p className="mb-2"><Mail size={14} className="me-2 text-primary" /> {data?.email}</p>
                  <p className="mb-0"><Building size={14} className="me-2 text-primary" /> {data?.plan_type} Plan</p>
                </div>
              </div>

              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                <h6 className="fw-bold mb-3 text-primary">Instructions</h6>
                <div className="bg-light p-3 rounded-3">
                   {isEditing ? (
                     <textarea className="form-control border-0 bg-transparent p-0 small" rows="4" value={editedInstructions} onChange={(e) => setEditedInstructions(e.target.value)} />
                   ) : (
                     data?.project_data?.instructions?.map((ins, idx) => (
                      <div key={idx} className="d-flex mb-1 align-items-start small">
                        <CheckCircle size={12} className="text-success me-2 mt-1" /> {ins}
                      </div>
                    ))
                   )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .avatar-large { width: 45px; height: 45px; background: #F4F7FE; color: #4318FF; display: flex; align-items: center; justify-content: center; border-radius: 12px; }
        .badge-status { padding: 4px 12px; border-radius: 6px; font-weight: 700; font-size: 10px; text-transform: uppercase; }
        .badge-status.draft { background: #E1EFFF; color: #0061FF; }
        .badge-status.pending { background: #FFF4E5; color: #FF8C00; }
        .badge-status.completed { background: #E2F9EB; color: #23BD5C; }
        .badge-status.in\\ progress { background: #EBEBFF; color: #4318FF; }
        .btn-white { background: white; border: none; color: #1B2559; }
        .leaflet-container { z-index: 1; border-radius: 20px; }
        .mapboxgl-canvas { position: absolute; }
.mapboxgl-map { position: absolute; top: 0; bottom: 0; left: 0; right: 0; }
      `}</style>
    </div>
  );
};

export default SubmissionDetail;